// ============================================================================
// responseGuard.js —— 单一权威响应 guard 核 (Step6 收敛)
// ----------------------------------------------------------------------------
// 取代散落 5 套近乎重复的 "anti-fake-success" token 分类器:
//   api/common.js · components/home/gameLaunch.js · views/details/home/gameResponse.js
//   views/layout/safeLayout.js · views/details/{mine,recharge}/safeFlow.js
//
// 策略(用户写死):
//   · 读接口 fail-OPEN —— 只认 3 个真失败信号:success===false / 明确 business fail / need_login。
//     readOnlyQuery / fallback / mock / diagnostics / technical* 一律不拦读。
//     哲学:读页面展示降级/占位数据 优于 整页阻断。历史 prod 事故(游戏不出)全因读路径误杀。
//   · 写接口 fail-CLOSED —— 严格正向成功 + 无 unavailable marker + 有意义写证明。
//     各领域写证明严格度由 config 注入,核不取最低公分母(防把支付/提现 fail-closed 弱成 fail-open=接受假充值)。
//
// 纯 ESM、零 app import → _diag/responseGuard.mjs 可原样拷做 node 回归。
// ============================================================================

// ---- 基础 ----------------------------------------------------------------
export function safeObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}
export function safeArray(value) {
  return Array.isArray(value) ? value : []
}
function hasOwn(source, key) {
  return source && typeof source === 'object' && Object.prototype.hasOwnProperty.call(source, key)
}
export function token(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}
// 去分隔符的紧凑形态:'read_only' / 'read only' / 'read-only' → 'readonly',折叠拼写变体爆炸
function compact(value) {
  return token(value).replace(/[\s_-]+/g, '')
}

const OK_TOKENS = ['ok', 'success', 'successful', 'succeed', 'succeeded', '0', '00', '0000', '000000', '200']
export function isSuccessToken(value) {
  return value === true || OK_TOKENS.includes(token(value))
}
// success 字段的"显式 false":只认布尔/字符串 false,不把数字 0 / 码 '0' 当失败(0 多为成功码)
function isFalseSuccess(value) {
  return value === false || token(value) === 'false'
}
function isFalseProofValue(value) {
  return value === false || value === 0 ||
    ['false', '0', 'no', 'off', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder'].includes(token(value))
}

// ---- envelope 解包 -------------------------------------------------------
// 注意:content 解包 与 read-层收集 都 **不含** diagnostics/meta/error 子树 —— 它们是可观测元数据
// (fallback:true / message:"...不可用..." 是正常降级说明),纳入会重演读路径误杀。
// 仅 write fail-closed 扫描时 (includeDiagnostics=true) 才下钻诊断子树查假成功。
const ENVELOPE_KEYS = ['content', 'data', 'payload', 'body', 'extra', 'page']
const LIST_KEYS = ['list', 'records', 'dataList', 'items', 'pageList', 'orderRecordList', 'capitalDetailsList', 'platformList']
const DIAGNOSTIC_KEYS = ['meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details', 'cause']
const READ_SCAN_KEYS = [...ENVELOPE_KEYS, ...LIST_KEYS]
const FULL_SCAN_KEYS = [...READ_SCAN_KEYS, ...DIAGNOSTIC_KEYS]
const MAX_DEPTH = 8

function normalizeBody(value) {
  if (Array.isArray(value)) return { list: value, dataList: value }
  return safeObject(value)
}
function hasPayloadFields(source) {
  if (!source || typeof source !== 'object') return false
  return Object.keys(source).some(key => !METADATA_KEYS.includes(key))
}
function firstEnvelopeBody(source) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object') return null
  const candidates = []
  let firstEmpty = null
  for (const key of READ_SCAN_KEYS) {
    const value = source[key]
    if (Array.isArray(value)) { candidates.push(value); continue }
    if (value && typeof value === 'object') {
      if (Object.keys(value).length) candidates.push(value)
      if (!firstEmpty) firstEmpty = value
    }
  }
  const populated = candidates.find(v => hasPayloadFields(unwrapEnvelopeBody(v)))
  if (populated) return populated
  const nonEmpty = candidates.find(v => Object.keys(normalizeBody(v)).length)
  if (nonEmpty) return nonEmpty
  if (firstEmpty) return firstEmpty
  if (LIST_KEYS.some(key => Array.isArray(source[key]))) return source
  return null
}
function unwrapEnvelopeBody(value, depth = 0) {
  const body = normalizeBody(value)
  if (!Object.keys(body).length || depth >= MAX_DEPTH) return body
  const nested = READ_SCAN_KEYS
    .map(key => body[key])
    .filter(n => n && (typeof n === 'object' || Array.isArray(n)))
  const populated = nested.find(n => hasPayloadFields(unwrapEnvelopeBody(n, depth + 1)))
  const ordered = populated ? [populated, ...nested.filter(n => n !== populated)] : nested
  for (const n of ordered) {
    const unwrapped = unwrapEnvelopeBody(n, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    READ_SCAN_KEYS.forEach(key => { delete parent[key] })
    return { ...parent, ...unwrapped }
  }
  return body
}
export function contentOf(response) {
  if (!response || typeof response !== 'object') return {}
  return unwrapEnvelopeBody(firstEnvelopeBody(response))
}
function collectBodies(source, keys, depth, out, seen) {
  if (!source || typeof source !== 'object' || depth >= MAX_DEPTH || seen.has(source)) return out
  seen.add(source)
  if (Array.isArray(source)) {
    source.forEach(item => collectBodies(item, keys, depth + 1, out, seen))
    return out
  }
  for (const key of keys) {
    const value = source[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) continue
    const body = normalizeBody(value)
    if (Object.keys(body).length) out.push(body)
    collectBodies(value, keys, depth + 1, out, seen)
  }
  return out
}
export function responseBodies(response, includeDiagnostics = false) {
  if (!response || typeof response !== 'object') return []
  return collectBodies(response, includeDiagnostics ? FULL_SCAN_KEYS : READ_SCAN_KEYS, 0, [], new Set())
}
function readLayers(response, includeDiagnostics = false) {
  return [response, contentOf(response), ...responseBodies(response, includeDiagnostics)]
    .filter(layer => layer && typeof layer === 'object')
}

const METADATA_KEYS = ['status', 'state', 'result', 'code', 'errorCode', 'success', 'msg', 'message', 'errorMsg', 'errorMessage', 'reason', 'desc', 'description', 'orderStatusName']

// ---- need_login (mode 无关) ----------------------------------------------
function isNeedLoginToken(value) {
  const t = token(value)
  if (!t) return false
  const c = compact(value)
  return c.includes('needlogin') ||
    c.includes('loginrequired') ||
    c.includes('unauthorized') ||
    c.includes('tokenexpired') ||
    c.includes('expiredtoken') ||
    c.includes('loginexpired') ||
    c.includes('sessionexpired') ||
    (c.includes('token') && c.includes('expired')) ||
    (c.includes('session') && c.includes('expired')) ||
    /(^|[^\d])401([^\d]|$)/.test(t)
}
function isNeedLoginFlag(value) {
  return value === true || value === 1 ||
    ['true', '1', 'yes', 'on'].includes(token(value)) ||
    isNeedLoginToken(value)
}
export function isNeedLogin(response) {
  if (!response) return false
  const layers = readLayers(response, true)
  if (layers.some(b => isNeedLoginFlag(b.needLogin) || isNeedLoginFlag(b.need_login))) return true
  return layers.some(b => [b.status, b.state, b.code, b.errorCode, b.msg, b.message, b.errorMsg, b.errorMessage, b.reason]
    .some(isNeedLoginToken))
}

// ---- 失败 / 不可用 token 分类器(仅 fail-closed/写 路径用;读路径不碰)----------
// 紧凑子串匹配,union of 5 套旧引擎(简繁中文 + 全拼写变体)。对写而言"宁可多拦"=fail-closed=安全。
const UNAVAILABLE_EXACT = ['fail', 'failed', 'failure', 'error', 'false', '-1', '400', '401', '403', '404', '500', 'rejected', 'reject', 'block', 'blocked', 'disabled', 'unavailable', 'need_login']
const UNAVAILABLE_SUBSTR = [
  'mock', 'fallback', 'stub', 'placeholder',
  'readonly', 'readonlyquery', 'readonlymode', 'readonlyaction',
  'readblocked', 'readunavailable', 'readfailure', 'readfailed',
  'unimplemented', 'notimplemented', 'notsupported', 'notconnected', 'unsupported',
  'dataunavailable', 'routeunavailable',
  'technicalerror', 'technicalexception', 'technicalmarker', 'backendfailure',
  'backendblocked', 'paymentbackendblocked', 'paymentstatusunconfirmed',
  'writeblocked', 'actionblocked', 'rewardbackendblocked',
  'falseproof', 'failclosed', 'queryonly', 'onlyquery', 'needlogin', 'loginrequired'
]
const UNAVAILABLE_CN = ['只读', '只讀', '未接入', '不可用', '技术错误', '技术异常', '只支持查询', '只支持查詢', '仅支持查询', '僅支持查詢']
export function isUnavailableToken(value) {
  const t = token(value)
  if (!t) return false
  if (UNAVAILABLE_EXACT.includes(t)) return true
  const c = compact(value)
  if (UNAVAILABLE_SUBSTR.some(s => c.includes(s))) return true
  return UNAVAILABLE_CN.some(s => t.includes(s))
}
// 明确的 business 失败状态(读/写都认):fail / error / rejected / cancelled / expired / timeout / disabled
function isFailureStatusToken(value) {
  const t = token(value)
  if (!t) return false
  if (['fail', 'failed', 'failure', 'error', 'rejected', 'reject', 'disabled', 'cancel', 'cancelled', 'canceled', 'expired', 'timeout'].includes(t)) return true
  return /(?:^|[\s_-])(?:fail|failed|failure|error|rejected)(?:$|[\s_-])/.test(t)
}
// 明确的失败码(排除 OK 码 0/200 与 need_login):4xx/5xx、e_ 前缀、含 fail 词
function isFailureCodeToken(value) {
  const t = token(value)
  if (!t || OK_TOKENS.includes(t) || isNeedLoginToken(t)) return false
  if (/^[45]\d{2,}$/.test(t)) return true
  return t.startsWith('e_') || isFailureStatusToken(t)
}

// ============================================================================
// 读原语:isReadOk —— FAIL-OPEN。只在 3 个真失败信号下返回 false,其余一律放行。
// ============================================================================
export function isReadOk(response) {
  if (!response || typeof response !== 'object') return true // 无可判定 → 放行,交给调用方渲染空
  if (isNeedLogin(response)) return false
  const layers = readLayers(response, false) // 浅层,**不**含 diagnostics
  if (layers.some(b => isFalseSuccess(b.success))) return false
  if (layers.some(b => [b.status, b.state, b.result].some(isFailureStatusToken))) return false
  if (layers.some(b => [b.code, b.errorCode].some(isFailureCodeToken))) return false
  return true
}
// 读失败原因(消息/诊断用):'' | 'needLogin' | 'fail'
export function readFailReason(response) {
  if (isNeedLogin(response)) return 'needLogin'
  return isReadOk(response) ? '' : 'fail'
}

// ============================================================================
// 写 fail-closed
// ============================================================================
// 不可用 marker 布尔旗键(union)
const MARKER_FLAG_KEYS = [
  'readOnly', 'readonly', 'read_only', 'readOnlyQuery', 'read_only_query', 'readOnlyMode', 'read_only_mode',
  'readOnlyAction', 'read_only_action', 'mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback',
  'fallback', 'fallbackUsed', 'fallback_used', 'stub', 'placeholder', 'placeholderMode', 'placeholder_mode',
  'placeholderResponse', 'placeholder_response', 'unimplemented', 'notImplemented', 'not_implemented',
  'notSupported', 'not_supported', 'notConnected', 'not_connected', 'unsupported',
  'dataUnavailable', 'data_unavailable', 'readUnavailable', 'read_unavailable', 'readFailure', 'read_failure',
  'readFailed', 'read_failed', 'readBlocked', 'read_blocked', 'routeUnavailable', 'route_unavailable',
  'technicalError', 'technical_error', 'technicalException', 'technical_exception', 'technicalMarker', 'technical_marker',
  'backendFailure', 'backend_failure', 'blocked', 'block', 'backendBlocked', 'backend_blocked',
  'paymentBackendBlocked', 'payment_backend_blocked', 'rewardBackendBlocked', 'reward_backend_blocked',
  'writeBlocked', 'write_blocked', 'actionBlocked', 'action_blocked', 'failClosed', 'fail_closed',
  'falseProof', 'false_proof', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed',
  'queryOnly', 'query_only', 'onlyQuery', 'only_query', 'unconfirmed', 'unconfirmedSuccess', 'unconfirmed_success',
  'needLogin', 'need_login'
]
function isActiveUnavailableFlag(value) {
  if (value === false || value === 0 || value == null) return false
  if (['false', '0', 'no', 'off'].includes(token(value))) return false
  return value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(token(value)) || isUnavailableToken(value)
}
const DEFAULT_FALSE_PROOF_KEYS = [
  'writeProof', 'write_proof', 'serverProof', 'server_proof', 'affectedProof', 'affected_proof',
  'affectedRowsProof', 'affected_rows_proof', 'statusProof', 'status_proof', 'orderProof', 'order_proof',
  'paymentProof', 'payment_proof', 'paymentUrlProof', 'payment_url_proof', 'payProof', 'pay_proof',
  'withdrawProof', 'withdraw_proof', 'rewardProof', 'reward_proof'
]
// 写 fail-closed 扫描:任一层(含 diagnostics)出现 假成功/不可用/显式假证明 → 不可用
export function hasUnavailableMarker(response, config = {}) {
  if (!response || typeof response !== 'object') return false
  const falseProofKeys = config.falseProofKeys || DEFAULT_FALSE_PROOF_KEYS
  return readLayers(response, true).some(b => {
    if (falseProofKeys.some(k => hasOwn(b, k) && isFalseProofValue(b[k]))) return true
    if (MARKER_FLAG_KEYS.some(k => hasOwn(b, k) && isActiveUnavailableFlag(b[k]))) return true
    if ([b.status, b.state, b.result, b.code, b.errorCode, b.success].some(isUnavailableToken)) return true
    if ([b.msg, b.message, b.errorMsg, b.errorMessage, b.reason].some(isUnavailableToken)) return true
    return false
  })
}
// 严格成功:正向 success token + 非 need_login + 无 unavailable marker + 无 business 失败
export function isStrictOk(response, config = {}) {
  if (!response || typeof response !== 'object') return false
  if (isNeedLogin(response)) return false
  if (hasUnavailableMarker(response, config)) return false
  const layers = readLayers(response, true)
  if (layers.some(b => isFalseSuccess(b.success))) return false
  if (layers.some(b => [b.status, b.state, b.result].some(isFailureStatusToken))) return false
  return layers.some(b => [b.status, b.state, b.result, b.code, b.errorCode, b.success].some(isSuccessToken))
}

// ---- 写证明(严格度由 config 注入,绝不取最低公分母)------------------------
function hasMeaningfulValue(value, allowObject = false) {
  if (Array.isArray(value)) return allowObject && value.length > 0
  if (value && typeof value === 'object') return allowObject && Object.keys(value).length > 0
  if (typeof value === 'boolean') return value
  const t = token(value)
  return t !== '' &&
    !['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder'].includes(t) &&
    !isUnavailableToken(value) &&
    !isFailureStatusToken(value)
}
// server-side 写证明:裸 true 不算证明(必须有具体 id/token/计数)
function hasMeaningfulServerProof(value, allowObject = false) {
  if (value === true || token(value) === 'true') return false
  return hasMeaningfulValue(value, allowObject)
}
function hasPositiveCount(value) {
  const t = token(value)
  if (!/^\d+$/.test(t)) return false
  return Number(t) > 0
}
// config: { serverProofKeys, looseProofKeys, countKeys, allowObjectProof }
export function hasMeaningfulProof(response, config = {}) {
  const serverProofKeys = config.serverProofKeys || []
  const looseProofKeys = config.looseProofKeys || []
  const countKeys = config.countKeys || ['affected', 'affectedRows', 'rowCount']
  const allowObject = !!config.allowObjectProof
  return readLayers(response, true).some(b =>
    serverProofKeys.some(k => hasOwn(b, k) && hasMeaningfulServerProof(b[k], allowObject)) ||
    looseProofKeys.some(k => hasOwn(b, k) && hasMeaningfulValue(b[k], allowObject)) ||
    countKeys.some(k => hasOwn(b, k) && hasPositiveCount(b[k]))
  )
}
// 写成功:严格成功 + 有意义写证明
export function isWriteOk(response, config = {}) {
  return isStrictOk(response, config) && hasMeaningfulProof(response, config)
}

// ---- 消息 ----------------------------------------------------------------
const TECHNICAL_MESSAGE_PATTERNS = [
  /network\s+error/i, /request\s+failed/i, /timeout(?:\s+of|\s*$)/i,
  /\bECONN[A-Z_]*\b/i, /\bETIMEDOUT\b/i, /\bENOTFOUND\b/i, /\bEHOSTUNREACH\b/i,
  /\baxios\b/i, /https?:\/\//i, /\blocalhost\b/i, /\b127\.0\.0\.1\b/,
  /<\s*\/?\s*(?:html|body|head|script|style|div|span|pre|br)\b/i,
  /\n\s*at\s+\S+/, /\bat\s+\S+\s+\([^)]*\)/,
  /\b(?:typeerror|referenceerror|syntaxerror|rangeerror|urierror)\b/i,
  /\b(?:java\.|sql(?:exception)?|select\s+.+\s+from|insert\s+into|update\s+.+\s+set|delete\s+from)\b/i
]
function isMojibakeMessage(value) {
  const message = String(value == null ? '' : value).trim()
  if (!message) return false
  if (message.includes('�')) return true
  const compactMessage = message.replace(/\s+/g, '')
  const markers = [
    '锛',
    '銆',
    '褰',
    '璐',
    '︽',
    '埛',
    '枡',
    '璇',
    '诲',
    '彇',
    '澶',
    '辫',
    '触',
    '绋',
    '嶅',
    '悗',
    '閲',
    '瘯',
    '鏈',
    '鍚',
    '佸',
    '妯',
    '鐢',
    '鏃',
    '堕',
    '鍦',
    '涓',
    '旀',
    '嫨',
    '湪',
    '鏄',
    '鐨'
  ]
  const markerHits = markers.reduce((count, marker) => count + (compactMessage.includes(marker) ? 1 : 0), 0)
  if (markerHits >= 3) return true
  const latinMojibakeHits = (message.match(/[ÃÂ]|â€|å|æ|è|ç/g) || []).length
  return latinMojibakeHits >= 3
}
function isTechnicalFailureMessage(value) {
  const message = String(value == null ? '' : value).trim()
  if (!message) return false
  if (isMojibakeMessage(message)) return true
  return TECHNICAL_MESSAGE_PATTERNS.some(p => p.test(message))
}
function firstMessage(response) {
  const keys = ['msg', 'message', 'errorMsg', 'errorMessage', 'reason', 'desc', 'description', 'orderStatusName']
  for (const layer of readLayers(response, true)) {
    for (const key of keys) {
      const v = layer[key]
      if (token(v)) return v
    }
  }
  return ''
}
export function failMessage(response, fallback = 'This feature is temporarily unavailable. Please try again later.') {
  if (isNeedLogin(response)) return 'Please log in to continue'
  const message = firstMessage(response)
  if (message && !isTechnicalFailureMessage(message) && !isUnavailableToken(message)) return message
  return fallback
}
